import { useState, useRef } from "react";
import { X, Image, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface ImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImagesAdd: (images: { id: string; file: File; blobUrl: string; name: string; }[]) => void;
}

export const ImageSelectionModal = ({ isOpen, onClose, onImagesAdd }: ImageSelectionModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const imageFiles = Array.from(files).filter(file => {
      // Support common phone camera formats: JPEG, PNG, HEIC, WebP
      return file.type.startsWith('image/') && 
             (file.type.includes('jpeg') || 
              file.type.includes('jpg') || 
              file.type.includes('png') || 
              file.type.includes('heic') || 
              file.type.includes('webp'));
    });

    if (imageFiles.length === 0) {
      toast({
        title: "No valid images",
        description: "Please select JPEG, PNG, HEIC, or WebP image files",
        variant: "destructive"
      });
      return;
    }

    if (imageFiles.length > 50) {
      toast({
        title: "Too many images",
        description: "Please select 50 images or fewer",
        variant: "destructive"
      });
      return;
    }

    setSelectedFiles(imageFiles);
  };

  const handleSubmit = () => {
    if (selectedFiles.length === 0) {
      toast({
        title: "No images selected",
        description: "Please select at least one image",
        variant: "destructive"
      });
      return;
    }

    const imageData = selectedFiles.map(file => ({
      id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      blobUrl: URL.createObjectURL(file),
      name: file.name
    }));

    onImagesAdd(imageData);
    
    toast({
      title: "Success",
      description: `${selectedFiles.length} image${selectedFiles.length > 1 ? 's' : ''} added successfully!`
    });

    handleClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-slate-900">Select Images</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* File Input Area */}
          <div
            className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600 mb-1">
              Click to select or drag and drop images
            </p>
            <p className="text-xs text-slate-500">
              JPEG, PNG, HEIC, WebP supported
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/heic,image/webp"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''} selected
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="text-xs text-slate-600 truncate">
                    {file.name}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedFiles.length === 0}
              className="flex-1"
            >
              Add Images
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
